<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import { X } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    description?: string;
    items?: string[];
  }>(),
  {
    description: '',
    items: () => [],
  }
);

const emit = defineEmits<{
  close: [];
}>();

function close() {
  emit('close');
}

function onEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open) {
    close();
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (typeof window === 'undefined') return;
    if (isOpen) {
      window.addEventListener('keydown', onEscape);
      document.body.style.overflow = 'hidden';
    } else {
      window.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('keydown', onEscape);
  document.body.style.overflow = '';
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="open"
        class="modal-content"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @click.self="close"
      >
        <div class="modal-content__panel">
          <div class="modal-content__header">
            <div class="modal-content__header-text">
              <h3 class="modal-content__title">{{ title }}</h3>
              <div v-if="$slots['header-subtitle']" class="modal-content__subtitle-wrap">
                <slot name="header-subtitle" />
              </div>
            </div>
            <button
              type="button"
              class="modal-content__close"
              aria-label="Close modal"
              @click="close"
            >
              <X class="modal-content__close-icon" aria-hidden="true" />
            </button>
          </div>
          <div class="modal-content__body">
            <p v-if="description" class="modal-content__description">
              {{ description }}
            </p>
            <ul v-if="items.length" class="modal-content__list">
              <li
                v-for="(item, index) in items"
                :key="`${index}-${item}`"
                class="modal-content__item"
              >
                {{ item }}
              </li>
            </ul>
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-content {
  position: fixed;
  inset: 0;
  z-index: 250;
  background: rgb(0 0 0 / 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-content__panel {
  width: 100%;
  max-width: 42rem;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

.modal-content__header {
  position: sticky;
  top: 0;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  min-height: 3.5rem;
}

.modal-content__header-text {
  flex: 1;
  min-width: 0;
}

.modal-content__title {
  margin: 0;
  font-size: 1.25rem;
  line-height: 1.4;
  color: #111827;
  font-weight: 600;
}

.modal-content__subtitle-wrap {
  margin-top: 0.25rem;
}

.modal-content__subtitle-wrap :deep(*) {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #6b7280;
  font-weight: 400;
}

.modal-content__close {
  border: 0;
  background: transparent;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.modal-content__close:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.modal-content__close-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.modal-content__body {
  padding: 1.25rem;
}

.modal-content__description {
  margin: 0 0 1rem;
  color: #4b5563;
  line-height: 1.6;
}

.modal-content__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-content__item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 0.875rem 1rem;
  color: #374151;
  line-height: 1.5;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (min-width: 640px) {
  .modal-content {
    padding: 1.5rem;
  }

  .modal-content__header {
    padding: 1rem 1.5rem;
  }

  .modal-content__body {
    padding: 1.5rem;
  }

  .modal-content__title {
    font-size: 1.25rem;
  }
}
</style>
